import { useState, useMemo, useEffect } from 'react';
import { useTemplates } from '@/contexts/TemplateContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Star,
  Pencil,
  Trash2,
  FileText,
  Copy,
  FolderOpen,
  Search,
  Clock,
  BarChart2,
  CalendarDays,
  Building2,
  Users,
} from 'lucide-react';
import type { ProjectTemplate } from '@/contexts/TemplateContext';
import { TemplateFormSheet } from '@/components/template-form-sheet';
import type { CreateTemplateData } from '@/types/template.types';
import { TEMPLATE_CATEGORIES } from '@/types/template.types';
import { ProjectCreationPanel } from '@/components/project-creation-panel';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortOption = 'most-used' | 'recently-created' | 'recently-used' | 'a-z' | 'z-a';
type CategoryFilter = 'all' | 'favourites' | typeof TEMPLATE_CATEGORIES[number];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCategoryColour(category?: string): string {
  const map: Record<string, string> = {
    Consulting: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'Fixed-Fee Delivery': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    Infrastructure: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    Marketing: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
    Internal: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300',
    Other: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-300',
  };
  return category ? (map[category] ?? map.Other) : map.Other;
}

function getPriorityColour(priority?: string): string {
  const map: Record<string, string> = {
    Critical: 'bg-error-100 text-error-700 dark:bg-error-900/40 dark:text-error-300',
    High: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    Medium: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
    Low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
  return priority ? (map[priority] ?? '') : '';
}

function formatLastUsed(iso?: string | null): string {
  if (!iso) return 'Never used';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Used today';
  if (diffDays === 1) return 'Used yesterday';
  if (diffDays < 30) return `Used ${diffDays}d ago`;
  if (diffDays < 365) return `Used ${Math.floor(diffDays / 30)}mo ago`;
  return `Used ${Math.floor(diffDays / 365)}y ago`;
}

// ─── Template Card ─────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: ProjectTemplate;
  onEdit: (template: ProjectTemplate) => void;
  onDuplicate: (template: ProjectTemplate) => void;
  onDelete: (id: string) => void;
  onUse: (template: ProjectTemplate) => void;
  onToggleFavorite: (id: string) => void;
}

function TemplateCard({
  template,
  onEdit,
  onDuplicate,
  onDelete,
  onUse,
  onToggleFavorite,
}: TemplateCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (confirmDelete) {
    return (
      <Card className="relative border-destructive/60 bg-destructive/5 flex flex-col">
        <CardContent className="flex flex-col items-center justify-center gap-4 py-8 flex-1">
          <div className="text-center">
            <Trash2 className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="font-semibold text-sm">Delete this template?</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[180px] mx-auto">
              "{template.templateName}" will be permanently removed.
            </p>
          </div>
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="flex-1"
              onClick={() => {
                onDelete(template.id);
                toast.success('Template deleted');
              }}
            >
              Confirm Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative flex flex-col group hover:shadow-md transition-shadow duration-150">
      {/* Favourite star — top right */}
      <button
        aria-label={template.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
        className="absolute top-3 right-3 z-10 rounded-full p-1 text-muted-foreground hover:text-warning-500 transition-colors"
        onClick={() => onToggleFavorite(template.id)}
      >
        <Star
          className={`h-4 w-4 transition-all ${
            template.isFavorite ? 'fill-warning-400 text-warning-400' : ''
          }`}
        />
      </button>

      <CardHeader className="pb-2 pr-10">
        {/* Category badge */}
        {template.category && (
          <span
            className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium mb-1.5 ${getCategoryColour(template.category)}`}
          >
            {template.category}
          </span>
        )}

        {/* Name */}
        <h3 className="font-semibold text-base leading-snug">{template.templateName}</h3>

        {/* Description */}
        {template.templateDescription && (
          <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
            {template.templateDescription}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex flex-col flex-1 gap-3 pt-0">
        {/* Badges row — priority + type */}
        {(template.priority || template.type) && (
          <div className="flex flex-wrap gap-1.5">
            {template.priority && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityColour(template.priority)}`}
              >
                {template.priority}
              </span>
            )}
            {template.type && (
              <Badge variant="outline" className="text-xs h-5">
                {template.type}
              </Badge>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {/* Usage count */}
          <span className="flex items-center gap-1">
            <BarChart2 className="h-3 w-3" />
            {template.usageCount === 1 ? '1 use' : `${template.usageCount} uses`}
          </span>

          {/* Default duration */}
          {template.defaultDurationDays && (
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {template.defaultDurationDays}d default
            </span>
          )}

          {/* Cost centres */}
          {template.costCentres && template.costCentres.length > 0 && (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {template.costCentres.length === 1
                ? '1 cost centre'
                : `${template.costCentres.length} cost centres`}
            </span>
          )}

          {/* Resource budget summary (8.15) */}
          {template.resourceBudget && template.resourceBudget.length > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {template.resourceBudget.length} role{template.resourceBudget.length !== 1 ? 's' : ''}
            </span>
          )}

          {/* Last used */}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatLastUsed(template.lastUsedAt)}
          </span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action buttons */}
        <div className="border-t pt-3 grid grid-cols-2 gap-1.5">
          <Button
            variant="default"
            size="sm"
            className="col-span-2"
            onClick={() => onUse(template)}
          >
            <FolderOpen className="h-3.5 w-3.5 mr-1.5" />
            Use Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => onEdit(template)}>
            <Pencil className="h-3 w-3 mr-1.5" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDuplicate(template)}>
            <Copy className="h-3 w-3 mr-1.5" />
            Duplicate
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="col-span-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="h-3 w-3 mr-1.5" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'most-used', label: 'Most Used' },
  { value: 'recently-created', label: 'Recently Created' },
  { value: 'recently-used', label: 'Recently Used' },
  { value: 'a-z', label: 'A – Z' },
  { value: 'z-a', label: 'Z – A' },
];

export default function ProjectTemplatesPage() {
  const { templates, addTemplate, updateTemplate, deleteTemplate, toggleFavorite } =
    useTemplates();

  // ── Loading state (simulates async fetch; ready for real API in Phase 6) ──
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(t);
  }, []);

  // ── Form sheet state ──
  const [formSheetOpen, setFormSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);

  // ── Project creation panel state ──
  const [creationPanelOpen, setCreationPanelOpen] = useState(false);
  const [useTemplateId, setUseTemplateId] = useState<string | undefined>(undefined);

  // ── Filter / sort state ──
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [sort, setSort] = useState<SortOption>('most-used');

  // ── Derived: tab counts ──
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { all: templates.length, favourites: 0 };
    for (const t of templates) {
      if (t.isFavorite) counts.favourites++;
      if (t.category) counts[t.category] = (counts[t.category] ?? 0) + 1;
    }
    return counts;
  }, [templates]);

  // ── Derived: filtered + sorted templates ──
  const displayedTemplates = useMemo(() => {
    let list = [...templates];

    // Category / favourites filter
    if (categoryFilter === 'favourites') {
      list = list.filter((t) => t.isFavorite);
    } else if (categoryFilter !== 'all') {
      list = list.filter((t) => t.category === categoryFilter);
    }

    // Search
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) =>
          t.templateName.toLowerCase().includes(q) ||
          (t.templateDescription ?? '').toLowerCase().includes(q),
      );
    }

    // Sort
    switch (sort) {
      case 'most-used':
        list.sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0));
        break;
      case 'recently-created':
        list.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'recently-used':
        list.sort((a, b) => {
          if (!a.lastUsedAt && !b.lastUsedAt) return 0;
          if (!a.lastUsedAt) return 1;
          if (!b.lastUsedAt) return -1;
          return new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime();
        });
        break;
      case 'a-z':
        list.sort((a, b) => a.templateName.localeCompare(b.templateName));
        break;
      case 'z-a':
        list.sort((a, b) => b.templateName.localeCompare(a.templateName));
        break;
    }

    return list;
  }, [templates, categoryFilter, search, sort]);

  const isFiltered = search.trim() !== '' || categoryFilter !== 'all';

  // ── Handlers ──

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setSheetMode('create');
    setFormSheetOpen(true);
  };

  const handleEditTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setSheetMode('edit');
    setFormSheetOpen(true);
  };

  const handleDuplicateTemplate = (template: ProjectTemplate) => {
    // Open the form sheet in create mode pre-populated with the duplicated data
    // so the user can rename before saving. This avoids needing addTemplate to
    // return the new ID (which would require a context API change).
    const syntheticTemplate: ProjectTemplate = {
      ...template,
      id: '',                                          // ignored in create mode
      templateName: `${template.templateName} (Copy)`,
      isFavorite: false,
      usageCount: 0,
      lastUsedAt: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSelectedTemplate(syntheticTemplate);
    setSheetMode('create');
    setFormSheetOpen(true);
    toast.info('Template duplicated — update the name before saving');
  };

  const handleDeleteTemplate = (id: string) => {
    deleteTemplate(id);
  };

  const handleUseTemplate = (template: ProjectTemplate) => {
    setUseTemplateId(template.id);
    setCreationPanelOpen(true);
  };

  const handleToggleFavorite = (id: string) => {
    const t = templates.find((tmpl) => tmpl.id === id);
    if (!t) return;
    toggleFavorite(id);
    toast.success(t.isFavorite ? 'Removed from favourites' : 'Added to favourites');
  };

  const handleSaveTemplate = (data: CreateTemplateData) => {
    if (sheetMode === 'create') {
      addTemplate({
        ...data,
        isFavorite: false,
        usageCount: 0,
        lastUsedAt: undefined,
      });
    } else if (selectedTemplate) {
      updateTemplate(selectedTemplate.id, { ...data });
    }
    // Toast is handled by TemplateFormSheet's handleSubmit — no double-toast here.
  };

  // ── Render ──

  return (
    <div className="space-y-6">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <PageHeaderBlock
          title="Project Templates"
          subtitle="Define reusable templates to speed up project creation and enforce consistency."
          size={20}
          showActions
          actions={
            <Button onClick={handleCreateTemplate} data-tour="project-templates-create-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          }
        />

        {/* ── Search + Sort toolbar ────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ── Category filter pills ────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2">
          {/* All */}
          <FilterPill
            label="All"
            count={tabCounts.all}
            active={categoryFilter === 'all'}
            onClick={() => setCategoryFilter('all')}
          />
          {/* Per-category */}
          {TEMPLATE_CATEGORIES.map((cat) =>
            tabCounts[cat] > 0 ? (
              <FilterPill
                key={cat}
                label={cat}
                count={tabCounts[cat]}
                active={categoryFilter === cat}
                onClick={() => setCategoryFilter(cat)}
              />
            ) : null,
          )}
          {/* Favourites */}
          {tabCounts.favourites > 0 && (
            <FilterPill
              label="⭐ Favourites"
              count={tabCounts.favourites}
              active={categoryFilter === 'favourites'}
              onClick={() => setCategoryFilter('favourites')}
            />
          )}
        </div>

        {/* ── Content ─────────────────────────────────────────────────────── */}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="flex flex-col">
                <CardHeader className="pb-2">
                  <Skeleton className="h-5 w-16 rounded-full mb-2" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full mt-1" />
                  <Skeleton className="h-4 w-2/3" />
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="flex gap-1.5">
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                  <div className="flex gap-3">
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-14" />
                  </div>
                  <Skeleton className="h-8 w-full mt-2" />
                  <div className="grid grid-cols-2 gap-1.5">
                    <Skeleton className="h-8" />
                    <Skeleton className="h-8" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No templates at all */}
        {!isLoading && templates.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
              <p className="text-muted-foreground text-center text-sm mb-6 max-w-xs">
                Create your first project template to streamline project creation and ensure
                consistency across your team.
              </p>
              <Button onClick={handleCreateTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Templates exist but nothing matches filter/search */}
        {!isLoading && templates.length > 0 && displayedTemplates.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Search className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No results found</h3>
              <p className="text-muted-foreground text-sm text-center mb-4">
                {search.trim()
                  ? `No templates match "${search.trim()}"`
                  : 'No templates in this category'}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setCategoryFilter('all');
                }}
              >
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Template grid */}
        {!isLoading && displayedTemplates.length > 0 && (
          <>
            {isFiltered && (
              <p className="text-sm text-muted-foreground -mb-2">
                Showing {displayedTemplates.length} of {templates.length} template
                {templates.length !== 1 ? 's' : ''}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="project-templates-list">
              {displayedTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleEditTemplate}
                  onDuplicate={handleDuplicateTemplate}
                  onDelete={handleDeleteTemplate}
                  onUse={handleUseTemplate}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>
          </>
        )}

      {/* ── Template Form Sheet ──────────────────────────────────────────── */}
      <TemplateFormSheet
        open={formSheetOpen}
        onOpenChange={setFormSheetOpen}
        mode={sheetMode}
        template={selectedTemplate ?? undefined}
        onSave={handleSaveTemplate}
      />

      {/* ── Project Creation Panel ───────────────────────────────────────── */}
      <ProjectCreationPanel
        open={creationPanelOpen}
        onOpenChange={(isOpen) => {
          setCreationPanelOpen(isOpen);
          if (!isOpen) setUseTemplateId(undefined);
        }}
        mode="create"
        initialTemplateId={useTemplateId}
      />
    </div>
  );
}

// ─── Filter Pill ───────────────────────────────────────────────────────────────

function FilterPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Filter by ${label}`}
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
      }`}
    >
      {label}
      <span
        className={`inline-flex items-center justify-center rounded-full h-4 min-w-4 px-1 text-[10px] font-semibold ${
          active ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}
      >
        {count}
      </span>
    </button>
  );
}
